import React, { useMemo, useState } from 'react';
import { Button, Checkbox, List, Select, Space } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { DefaultOptionType } from 'antd/lib/select';
import { keyBy, sortBy } from 'lodash';
import cx from 'classnames';

import { Team } from '@cbinsights/adminservice/adminservice';
import { useGetAllTeams } from 'client/modules/common/hooks/queries/admin';
import {
  useListTeamClients,
  useSoftDeleteClient,
  useUndeleteClient,
} from 'client/modules/external-api/hooks/queries/external-api';

import {
  ApiApplication,
  Client,
  Dataset,
  IdPackageType,
  ListTeamClientsResponse,
} from '@cbinsights/externalapiservice/externalapiservice';
import { useQueryClient } from 'react-query';
import { packageTypeToLabel } from './constants';
import AddCreditsButton from './AddCreditsButton';
import CreateClientButton, { datasetsOptionType } from './CreateClientButton';
import EditClientButton from './EditClientButton';

const { Option } = Select;

const ToggleClientButton = ({
  className,
  client,
}: {
  className?: string;
  client: Client;
}) => {
  const queryClient = useQueryClient();
  const softDelete = useSoftDeleteClient(client);
  const undelete = useUndeleteClient(client);
  const isLoading = softDelete.isLoading || undelete.isLoading;

  const handleClick = async () => {
    if (client?.is_active) {
      await softDelete.mutateAsync();
    } else {
      await undelete.mutateAsync();
    }

    await queryClient.invalidateQueries([
      'externalapiservice/ListTeamClients',
      Number(client.name),
    ]);
  };
  return (
    <Button
      className={className}
      danger={client?.is_active}
      disabled={isLoading}
      loading={isLoading}
      onClick={handleClick}
      type="ghost"
    >
      {client?.is_active ? 'Deactivate' : 'Activate'}
    </Button>
  );
};

export const ClientDetail = ({
  response,
  isFetching,
}: {
  response?: ListTeamClientsResponse;
  isFetching: boolean;
}) => {
  return (
    <>
      {response?.clients?.length === 0 && !isFetching && (
        <p className="font-medium">No existing client.</p>
      )}

      <List loading={isFetching} size="small" className="my-2">
        {response?.clients?.map((client) => {
          const expirationDate = new Date(client.expiration_date);
          return (
            <List.Item key={client.client_id}>
              <ul>
                <li className="flex leading-[20px]">
                  <div className="font-medium w-[150px]">Client ID:</div>
                  {client.client_id}
                </li>
                {client.client_secret && (
                  <li className="flex items-center">
                    <div className="font-medium w-[150px]">Client secret:</div>
                    <Button
                      className="flex items-center font-medium max-w-[300px] mt-1"
                      ghost
                      icon={<CopyOutlined />}
                      onClick={() =>
                        navigator.clipboard.writeText(client.client_secret)
                      }
                      title={client.client_secret}
                      type="primary"
                    >
                      <span className="truncate">{client.client_secret}</span>
                    </Button>
                  </li>
                )}
                <li className="flex leading-[20px]">
                  <div className="font-medium w-[150px]">
                    Available credits:
                  </div>
                  {client.available_credits}
                </li>
                <li className="flex leading-[20px]">
                  <div className="font-medium w-[150px]">Total credits:</div>
                  {client.total_credits}
                </li>
                <li className="flex leading-[20px]">
                  <div className="font-medium w-[150px]">
                    Expiration date (UTC):
                  </div>
                  {Number.isNaN(expirationDate.getTime()) ||
                  expirationDate.getUTCFullYear() <= 1
                    ? '—'
                    : new Intl.DateTimeFormat('en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        second: 'numeric',
                        timeZone: 'UTC',
                      }).format(expirationDate)}
                </li>
                <li className="flex leading-[20px]">
                  <div className="font-medium w-[150px]">Package type:</div>
                  <span>{packageTypeToLabel[client.id_package_type]}</span>
                  {client.id_package_type !== IdPackageType.V2_ONLY &&
                    client?.datasets.length > 0 && (
                      <span className="ml-1">includes v2 API</span>
                    )}
                </li>
                {client?.datasets?.length > 0 && (
                  <li className="flex leading-[20px]">
                    <div className="font-medium w-[150px]">Datasets:</div>
                    <ul>
                      {client?.datasets?.map((dataset) => (
                        <li key={dataset}>
                          {datasetsOptionType.filter(
                            (option) => option.value === dataset
                          )?.[0]?.label || dataset}
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
                {client?.datasets.includes(Dataset.SCOUTING_REPORTS) && (
                  <>
                    <li className="flex leading-[20px] mt-2">
                      <div className="font-bold w-[150px]">
                        Scouting Reports
                      </div>
                    </li>
                    <li className="flex leading-[20px]">
                      <div className="w-[150px]">Credit allocation:</div>
                      {client.scouting_report_credits_allocation}
                    </li>
                    <li className="flex leading-[20px] mb-2">
                      <div className="w-[150px]">Allowed overage:</div>
                      {client.scouting_report_allowed_overage}
                    </li>
                  </>
                )}
                {client?.datasets.includes(Dataset.CHAT_CBI) && (
                  <>
                    <li className="flex leading-[20px] mt-2">
                      <div className="font-bold w-[150px]">ChatCBI</div>
                    </li>
                    <li className="flex leading-[20px]">
                      <div className="w-[150px]">Credit allocation:</div>
                      {client.chat_cbi_credits_allocation}
                    </li>
                    <li className="flex leading-[20px] mb-2">
                      <div className="w-[150px]">Allowed overage:</div>
                      {client.chat_cbi_allowed_overage}
                    </li>
                  </>
                )}
                <li
                  className={cx(
                    'flex leading-[20px]',
                    !client.is_active && 'text-red-600'
                  )}
                >
                  <div className="font-medium w-[150px]">Active: </div>
                  {client.is_active ? 'True' : 'False'}
                </li>
                <li className="flex leading-[20px]">
                  <div className="font-medium w-[150px]">Application: </div>
                  {client.api_application ===
                    ApiApplication.API_APPLICATION_UNSPECIFIED && 'Unspecified'}
                  {client.api_application ===
                    ApiApplication.API_APPLICATION_DATAFEEDS && 'Data Feeds'}
                  {client.api_application ===
                    ApiApplication.API_APPLICATION_CLAY && 'Clay'}
                </li>
                <li>
                  <div className="mt-4 flex space-x-4">
                    <EditClientButton client={client} />
                    <ToggleClientButton client={client} />
                  </div>
                </li>
              </ul>
            </List.Item>
          );
        })}
      </List>
    </>
  );
};

export const TeamClient = ({ team }: { team: Team }): React.ReactElement => {
  const { data: response, isFetching } = useListTeamClients(
    Number(team.idTeam)
  );
  const initialCredits = response?.clients[0]?.available_credits || 0;

  return (
    <div
      className={cx(
        'mt-8 p-6 border border-gray-300 rounded min-h-[160px]',
        !response && 'flex flex-col'
      )}
    >
      <h3 className="font-bold text-[16px] mb-3 flex justify-between">
        {team.name}{' '}
        <Space>
          {response?.clients[0] && (
            <AddCreditsButton client={response?.clients[0]} />
          )}
          <CreateClientButton
            key={team?.idTeam}
            idTeam={team?.idTeam}
            name={team?.name}
            initialCredits={parseInt(String(initialCredits), 10)}
          />
        </Space>
      </h3>
      <ClientDetail response={response} isFetching={isFetching} />
    </div>
  );
};

const ExternalApiPage = (): React.ReactElement => {
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [showTestTeams, setShowTestTeams] = useState(false);
  const [isSortByIdTeam, setIsSortByIdTeam] = useState(false);
  const { data: teams, isLoading: isLoadingTeams } = useGetAllTeams();

  const teamsById = useMemo(() => keyBy(teams, 'idTeam'), [teams]);
  const teamOptions = useMemo(() => {
    const list = teams?.filter(
      (t) => !!t.name && !t.isArchived && (showTestTeams || !t.isTestTeam)
    );
    // Response already sorted by idTeam by default
    return isSortByIdTeam ? list : sortBy(list, 'name');
  }, [teams, showTestTeams, isSortByIdTeam]);

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">External API Clients</h1>
      <div className="md:flex max-w-[930px] mt-8">
        <div className="md:w-[400px]">
          <p className="font-bold">Team Name</p>
          <Select
            allowClear
            className="w-full mt-2"
            disabled={isLoadingTeams}
            filterOption={(inputValue: string, option?: DefaultOptionType) =>
              !!option &&
              (Number(inputValue) === option.value ||
                !!option.label
                  ?.toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase()))
            }
            mode="multiple"
            onClear={() => setSelectedTeams([])}
            onChange={(values) => {
              const newTeams = (values as number[])
                .map((idTeam) => teamsById[idTeam])
                .reverse();
              setSelectedTeams(newTeams);
            }}
            placeholder="Search for team"
            showSearch
          >
            {teamOptions?.map((t) => (
              <Option
                key={t.idTeam}
                label={t.name}
                title={t.name}
                value={t.idTeam}
              >
                <div className="flex justify-between">
                  <span className="truncate flex-shrink-1">{t.name}</span>
                  <span className="text-gray-700 text-xs ml-2">
                    idTeam: {t.idTeam}
                  </span>
                </div>
              </Option>
            ))}
          </Select>
          <div className="mt-2">
            <Checkbox
              checked={isSortByIdTeam}
              onChange={() => setIsSortByIdTeam(!isSortByIdTeam)}
            >
              Sort by idTeam
            </Checkbox>
            <Checkbox
              checked={showTestTeams}
              className="ml-3"
              onChange={() => setShowTestTeams(!showTestTeams)}
            >
              Include test teams
            </Checkbox>
          </div>
        </div>
        <div className="md:ml-8 flex-grow">
          {selectedTeams.map((team) => (
            <TeamClient key={team.idTeam} team={team} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExternalApiPage;
